import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("No verification token provided.");
      return;
    }

    const verify = async () => {
      try {
        const { data } = await axios.post(`${API}/api/auth/verify-email`, { token });
        setStatus("success");
        setMessage(data.message || "Email verified successfully!");
      } catch (e) {
        setStatus("error");
        const detail = e.response?.data?.detail;
        setMessage(typeof detail === "string" ? detail : "Verification failed. The link may have expired.");
      }
    };

    verify();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}>
      <div className="max-w-md w-full mx-4">
        <div className="border border-[#1A1A1A] bg-[#0D0D0D] p-12 text-center">
          {status === "verifying" && (
            <>
              <Loader2 className="h-12 w-12 text-[#39FF14] mx-auto mb-6 animate-spin" strokeWidth={1.5} />
              <h1 className="text-2xl font-bold uppercase tracking-tight text-[#F2F2F2] mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
                Verifying Email
              </h1>
              <p className="text-[#A1A1A1] text-sm">Please wait...</p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle className="h-12 w-12 text-[#39FF14] mx-auto mb-6" strokeWidth={1.5} />
              <h1 data-testid="verify-success" className="text-2xl font-bold uppercase tracking-tight text-[#F2F2F2] mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
                Email Verified
              </h1>
              <p className="text-[#A1A1A1] text-sm mb-8">{message}</p>
              <Link to="/products">
                <Button className="w-full bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold py-6">
                  Start Shopping
                </Button>
              </Link>
            </>
          )}

          {status === "error" && (
            <>
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-6" strokeWidth={1.5} />
              <h1 data-testid="verify-error" className="text-2xl font-bold uppercase tracking-tight text-[#F2F2F2] mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
                Verification Failed
              </h1>
              <p className="text-[#A1A1A1] text-sm mb-8">{message}</p>
              <Link to="/">
                <Button className="w-full bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold py-6">
                  Go Home
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
